/**
 * 🏠 App.js - TAM MÜKƏMMƏL VERSİYA
 * 
 * SignalR Provider ilə bütün real-time funksionallıq inteqrasiya edilib.
 * 
 * Xüsusiyyətlər:
 * - Real-time bid updates
 * - Real-time notifications  
 * - Real-time balance updates
 * - Auction events (ended, extended, won, outbid)
 * 
 * @version 5.0.0
 */

import { createContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import Routing from "./Routing";
import axios from "axios";
import Loading from "./components/Loading";
// ✅ DÜZGÜN import yolu
import { SignalRProvider, useSignalR } from "./components/services/SignalRContext";

export const HerracContext = createContext();

// ═══════════════════════════════════════════════════════════════
// 📱 APP CONTENT - SignalR context içində olmalıdır
// ═══════════════════════════════════════════════════════════════
function AppContent() {
  // ════════════════════════════════════════════════════════════
  // STATE MANAGEMENT
  // ════════════════════════════════════════════════════════════
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // Məhsul və filtr dataları
  const [productsData, setProductsData] = useState([]);
  const [automobileData, setAutomobileData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [brandsData, setBrandsData] = useState([]);
  const [modelsData, setModelsData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);
  const [sparePartsData, setSparePartsData] = useState([]);
  const [partTypeData, setPartTypeData] = useState([]);
  const [conditionData, setConditionData] = useState([]);
  const [fuelTypeData, setFuelTypeData] = useState([]);
  const [truckBrandData, setTruckBrandData] = useState([]);
  const [truckModelData, setTruckModelData] = useState([]);
  const [truckData, setTruckData] = useState([]);
  const [cabinTypeData, setCabinTypeData] = useState([]);
  const [transmissionTypeData, setTransmissionTypeData] = useState([]);
  const [trailerTypeData, setTrailerTypeData] = useState([]);

  // Axtarış və filtrasiya
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [countData, setCountData] = useState(18);

  // Refs
  const isInitialMount = useRef(true);
  const hasLoadedData = useRef(false);

  // ════════════════════════════════════════════════════════════
  // 🔌 SIGNALR HOOKS - BÜTÜN EVENTLƏR
  // ════════════════════════════════════════════════════════════
  const { 
    // Connection
    connectNotifications, 
    isAuctionConnected,
    isNotificationConnected,
    
    // Notification events
    onNotification, 
    onBalanceUpdated,
    onNotificationCountUpdate,
    
    // Auction events
    onUserOutbid,
    onAuctionEnded,
    onAuctionWon,
    
    // Utility
    getStatus
  } = useSignalR();

  // ════════════════════════════════════════════════════════════
  // 💰 USER BALANCE
  // ════════════════════════════════════════════════════════════
  const getUserBalance = useCallback(async (userId) => {
    if (!userId) return;
    
    try {
      const res = await axios.get(
        `https://api.herracim.az/api/BidDeposit/GetBalance/${userId}`
      );
      setBalance(Number(res.data.balance) || 0);
    } catch (err) {
      console.error('Balance fetch error:', err);
      setBalance(0);
    }
  }, []);

  // ════════════════════════════════════════════════════════════
  // 🔔 SIGNALR - BALANCE UPDATES (Real-time)
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;

    const unsubBalance = onBalanceUpdated((data) => {
      // Bu istifadəçi üçün balans yeniləməsi
      if (data.userId === user.userId || data.relatedAuctionId) {
        console.log('💰 Balans yeniləndi (SignalR):', data);
        
        // Əgər data.balance varsa, birbaşa set et
        if (typeof data.balance === 'number') {
          setBalance(data.balance);
        } else {
          // Yoxdursa, API-dən yenidən al
          getUserBalance(user.userId);
        }
      }
    });

    return () => unsubBalance();
  }, [user, onBalanceUpdated, getUserBalance]);

  // ════════════════════════════════════════════════════════════
  // 🔔 SIGNALR - NOTIFICATIONS (Real-time)
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;

    // NotificationHub-a qoşul
    connectNotifications();

    // Yeni bildiriş gəldikdə
    const unsubNotif = onNotification((notification) => {
      console.log('🔔 Yeni bildiriş (SignalR):', notification);
      setUnreadNotificationCount(prev => prev + 1);

      // Balans ilə əlaqəli bildirişlərdə balansı yenilə
      if (notification.type === 'deposit_refunded' || 
          notification.type === 'deposit_confirmed' ||
          notification.type === 'balance_updated') {
        getUserBalance(user.userId);
      }
    });

    // Bildiriş sayı yeniləndikdə
    const unsubCount = onNotificationCountUpdate((data) => {
      if (data && typeof data.unreadCount === 'number') {
        setUnreadNotificationCount(data.unreadCount);
      }
    });

    return () => {
      unsubNotif();
      unsubCount();
    };
  }, [user, connectNotifications, onNotification, onNotificationCountUpdate, getUserBalance]);

  // ════════════════════════════════════════════════════════════
  // 😤 SIGNALR - USER OUTBID (Təklif keçildi)
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;

    const unsubOutbid = onUserOutbid((data) => {
      console.log('😤 Təklifiniz keçildi (SignalR):', data);
      // Toast notification göstərmək üçün global event emit edə bilərsiniz
      // window.dispatchEvent(new CustomEvent('userOutbid', { detail: data }));
    });

    return () => unsubOutbid();
  }, [user, onUserOutbid]);

  // ════════════════════════════════════════════════════════════
  // 🏁 SIGNALR - AUCTION ENDED
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;

    const unsubEnded = onAuctionEnded((data) => {
      console.log('🏁 Hərrac bitdi (SignalR):', data);
      // Balansı yenilə (deposit qaytarıla bilər)
      getUserBalance(user.userId);
    });

    return () => unsubEnded();
  }, [user, onAuctionEnded, getUserBalance]);

  // ════════════════════════════════════════════════════════════
  // 🏆 SIGNALR - AUCTION WON
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user) return;

    const unsubWon = onAuctionWon((data) => {
      console.log('🏆 Hərrac qazandınız (SignalR):', data);
      // window.dispatchEvent(new CustomEvent('auctionWon', { detail: data }));
    });

    return () => unsubWon();
  }, [user, onAuctionWon]);

  // ════════════════════════════════════════════════════════════
  // 👤 GET USER DATA - İLK YÜKLƏMƏ
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    const getUserData = async () => {
      try {
        const response = await axios.get(
          "https://api.herracim.az/api/user/current",
          { withCredentials: true }
        );

        setUser(response.data);

        if (response.data?.userId) {
          await getUserBalance(response.data.userId);
          
          // İlkin bildiriş sayını API-dən al
          try {
            const notifRes = await axios.get(
              "https://api.herracim.az/api/Notification/unread-count",
              { withCredentials: true }
            );
            setUnreadNotificationCount(notifRes.data?.unreadCount || 0);
          } catch {
            // Səssiz qal
          }
        }
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    getUserData();
  }, [getUserBalance]);

  // ════════════════════════════════════════════════════════════
  // 📦 FETCH REFERENCE DATA
  // ════════════════════════════════════════════════════════════
  const fetchData = useCallback(async () => {
    if (hasLoadedData.current) return;

    try {
      const endpoints = [
        "Automobile", "Category", "Brand", "Model", "City",
        "SpareParts", "PartType", "Condition", "FuelType",
        "TruckBrand", "TruckModel", "Truck", "CabinType",
        "TransmissionType", "TrailerType",
      ];

      const responses = await Promise.all(
        endpoints.map(endpoint =>
          axios.get(`https://api.herracim.az/api/${endpoint}`)
        )
      );

      setAutomobileData(responses[0].data);
      setCategoriesData(responses[1].data);
      setBrandsData(responses[2].data);
      setModelsData(responses[3].data);
      setCitiesData(responses[4].data);
      setSparePartsData(responses[5].data);
      setPartTypeData(responses[6].data);
      setConditionData(responses[7].data);
      setFuelTypeData(responses[8].data);
      setTruckBrandData(responses[9].data);
      setTruckModelData(responses[10].data);
      setTruckData(responses[11].data);
      setCabinTypeData(responses[12].data);
      setTransmissionTypeData(responses[13].data);
      setTrailerTypeData(responses[14].data);

      hasLoadedData.current = true;
    } catch (error) {
      console.error("Data fetch error:", error);
    }
  }, []);

  // ════════════════════════════════════════════════════════════
  // 📦 FETCH PRODUCTS
  // ════════════════════════════════════════════════════════════
  const fetchProducts = useCallback(
    async (categoryId, subCategoryId, count, search, activeOnly = false) => {
      try {
        const params = {
          categoryId: categoryId ?? selectedCategory,
          subCategoryId: subCategoryId ?? selectedSubcategory,
          count: count ?? countData,
          search: search ?? searchQuery,
          activeOnly: activeOnly ?? false,
        };

        let url = `https://api.herracim.az/api/ProductBase?count=${params.count}&activeOnly=${params.activeOnly}`;

        if (params.subCategoryId) {
          url += `&subCategoryId=${params.subCategoryId}`;
        } else if (params.categoryId) {
          url += `&categoryId=${params.categoryId}`;
        }

        if (params.search) {
          url += `&name=${encodeURIComponent(params.search)}`;
        }

        const response = await axios.get(url);
        const products = response.data.products || response.data.Products || [];

        if (params.search) {
          setSearchResults(products);
        } else {
          setProductsData(products);
        }
        
        return products;
      } catch (error) {
        console.error("Products fetch error:", error);
        setProductsData([]);
        return [];
      }
    },
    [selectedCategory, selectedSubcategory, countData, searchQuery]
  );

  // İlk yükləmə
  useEffect(() => {
    const initializeApp = async () => {
      await fetchData();
      await fetchProducts(null, null, 18, "", false);
    };
    initializeApp();
  }, [fetchData, fetchProducts]);

  // Filtr dəyişikliklərində debounce ilə yenilə
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (loading) return;

    const debounceTimer = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [selectedCategory, selectedSubcategory, countData, searchQuery, loading, fetchProducts]);

  // ════════════════════════════════════════════════════════════
  // 🔍 DEBUG - Development modu üçün
  // ════════════════════════════════════════════════════════════
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // SignalR statusunu konsola yaz
      const logStatus = () => {
        const status = getStatus();
        console.log('🔌 SignalR Status:', status);
      };

      // Hər 30 saniyədə bir status yaz
      const interval = setInterval(logStatus, 30000);
      
      // İlk status
      logStatus();

      return () => clearInterval(interval);
    }
  }, [getStatus]);

  // ════════════════════════════════════════════════════════════
  // 📤 CONTEXT VALUE
  // ════════════════════════════════════════════════════════════
  const herracDataBase = useMemo(() => ({
    // User & Balance
    user, setUser,
    balance, setBalance,
    getUserBalance,

    // Notifications
    unreadNotificationCount, setUnreadNotificationCount,

    // SignalR status
    isSignalRConnected: isAuctionConnected,
    isNotificationConnected,

    // Products
    productsData, setProductsData,
    countData, setCountData,
    fetchProducts,

    // Reference Data
    automobileData,
    categoriesData,
    brandsData,
    modelsData,
    citiesData,
    sparePartsData,
    partTypeData,
    conditionData,
    fuelTypeData,
    truckBrandData,
    truckModelData,
    truckData,
    cabinTypeData,
    transmissionTypeData,
    trailerTypeData,

    // Search & Filter
    searchResults, setSearchResults,
    searchQuery, setSearchQuery,
    selectedCategory, setSelectedCategory,
    selectedSubcategory, setSelectedSubcategory,

    // Utility
    loading, setLoading,
  }), [
    user, balance, unreadNotificationCount, 
    isAuctionConnected, isNotificationConnected,
    productsData, countData,
    automobileData, categoriesData, brandsData, modelsData, citiesData,
    sparePartsData, partTypeData, conditionData, fuelTypeData,
    truckBrandData, truckModelData, truckData, cabinTypeData,
    transmissionTypeData, trailerTypeData,
    searchResults, searchQuery, selectedCategory, selectedSubcategory,
    loading, getUserBalance, fetchProducts
  ]);

  return (
    <HerracContext.Provider value={herracDataBase}>
      <div className="App">
        {loading ? <Loading /> : <Routing />}
      </div>
    </HerracContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════
// 🚀 MAIN APP - SignalR Provider ilə sarılmış
// ═══════════════════════════════════════════════════════════════
function App() {
  return (
    <SignalRProvider>
      <AppContent />
    </SignalRProvider>
  );
}

export default App;